import { CancelTokenSource } from "axios";
import { axiosSecuredInstance, cancelToken } from "configurations/axiosConfig";
import { InventorySharedSearchCriteria } from "entities/UIModel/InventorySharedSearchCriteria";
import { InventoryShared } from "entities/ApiModel/InventoryShared";
import { InventorySharedExportCriteria } from "entities/ApiModel/InventorySharedExportCriteria";
import { InventorySharedUI } from "entities/UIModel/InventorySharedUI";
import { groupBy as _groupBy } from "lodash";
import { createHook, createStore, StoreActionApi } from "react-sweet-state";
import { DownloadFileType } from "utils/data/enum";
import { startNotifying, stopNotifying } from "utils/services/ContextProgressNotifierService";
import { dateTimeAsFilename, downloadByBase64, getBase64DataType } from "utils/shared";

const SHARE_LOOKUP_PROGRESS_ID = "Share-Lookup";

type State = {
    progressPercent: number;
    isLoading: boolean;
    agenciesData: InventorySharedUI[];
    requestTimeoutMs: number;
    error: any;
    axiosCancelToken?: CancelTokenSource;
    selectedAgencyItems: InventorySharedUI[];
}

type Actions = typeof actions;

const initialState: State = {
    progressPercent: 0,
    isLoading: false,
    agenciesData: [],
    requestTimeoutMs: 0,
    error: null,
    axiosCancelToken: undefined,
    selectedAgencyItems: [],
}

type StoreApi = StoreActionApi<State>;

// The API returns one row per (assigned location x sharing location) pair, but
// InventoryShareDetail_GetListByKey counts and pages by *distinct assigned location*,
// stamping every row of one location with the same CurrentRow (its ROW_NUMBER over the
// paged set). Grouping on CurrentRow reproduces the server's own partition, so a page of
// `listCount` locations always renders as exactly that many grid rows and can never
// disagree with the TotalRows/TotalPages driving the footer.
export const setSharedAgencies = (records: InventoryShared[]) => (): InventorySharedUI[] => {
    const groupedByAssignedLocation = _groupBy(records, "CurrentRow");

    return Object.values(groupedByAssignedLocation).map((group) => {
        const [assignedLocation] = group;

        return {
            uniqueIdentifier: assignedLocation.AssignedLocationUniqueID,
            inventoryShareDetailID: assignedLocation.InventoryShareDetailID,
            inventoryShareID: assignedLocation.InventoryShareID,
            assignedCompanyID: assignedLocation.AssignedCompanyID,
            assignedCompanyName: assignedLocation.AssignedCompanyName,
            assignedCompanyLegacyID: assignedLocation.AssignedCompanyLegacyID,
            assignedLocationUniqueID: assignedLocation.AssignedLocationUniqueID,
            assignedLocationLegacyID: assignedLocation.AssignedLocationLegacyID,
            assignedLocationDisplayName: assignedLocation.AssignedLocationDisplayName,
            assignedLocationStateCode: assignedLocation.AssignedLocationStateCode,
            assignedLocationStateAbbr: assignedLocation.AssignedLocationStateAbbr,
            receivingAgencies: group.map((agent) => ({
                sharedCompanyID: agent.SharedCompanyID,
                sharedCompanyName: agent.SharedCompanyName,
                sharedCompanyLegacyID: agent.SharedCompanyLegacyID,
                sharedLocationUniqueID: agent.SharedLocationUniqueID,
                sharedLocationLegacyID: agent.SharedLocationLegacyID,
                sharedLocationDisplayName: agent.SharedLocationDisplayName,
                sharedLocationStateCode: agent.SharedLocationStateCode,
                sharedLocationStateAbbr: agent.SharedLocationStateAbbr,
                inventoryShareDetailID: agent.InventoryShareDetailID,
                inventoryShareID: agent.InventoryShareID,
            })),
            currentRow: assignedLocation.CurrentRow,
            totalRows: assignedLocation.TotalRows,
            totalPages: assignedLocation.TotalPages,
            currentPage: assignedLocation.CurrentPage,
            rowsPerPage: assignedLocation.RowsPerPage,
            identityKey: assignedLocation.IdentityKey,
            integrationKey: assignedLocation.IntegrationKey,
            hasGUIDChanged: assignedLocation.HasGUIDChanged,
            isNew: assignedLocation.IsNew,
            isDeleted: assignedLocation.IsDeleted,
            isDirty: assignedLocation.IsDirty,
            isReadOnly: assignedLocation.IsReadOnly,
            partitionYear: assignedLocation.PartitionYear,
            active: assignedLocation.Active,
        };
    });
};

const startProgressNotififer =
    () =>
        (storeApi: StoreApi) => {
            const handleUpdateProgress = (percent: number | undefined) =>
                storeApi.setState({ progressPercent: percent });
            startNotifying(SHARE_LOOKUP_PROGRESS_ID, handleUpdateProgress);
        };

const stopProgressNotififer =
    () =>
        (storeApi: StoreApi) => {
            const handleUpdateProgress = (percent: number | undefined) =>
                storeApi.setState({ progressPercent: percent });
            stopNotifying(SHARE_LOOKUP_PROGRESS_ID, handleUpdateProgress);
        };

const actions = {
    getData: (params: InventorySharedSearchCriteria) => async ({ setState, dispatch, getState }: StoreApi) => {
       try{
          dispatch(startProgressNotififer());
          setState({ isLoading: true });
          const token = getState().axiosCancelToken;
          if (token) {
            token.cancel("ModifyContext was cancelled due to new request");
          }
          const newCancelToken = cancelToken.source();
          setState({ error: undefined, axiosCancelToken: newCancelToken });

          const { assignedAgent, ...restParams } = params;
          const { data } = await axiosSecuredInstance.get<InventoryShared[]>("/InventoryShare/SearchByKey", {
            headers: {
                'Cache-Control': 'no-cache',
             },
            params: { ...restParams, key: assignedAgent },
            cancelToken: newCancelToken.token,
          });

        dispatch(stopProgressNotififer());
        setState({ isLoading: false });

        return dispatch(setSharedAgencies(data));
       } catch(error: any){
        dispatch(stopProgressNotififer());
        setState({ error, isLoading: false });
        // Returning undefined (not []) lets the caller's `records !== undefined`
        // guard skip the whole block. A cancelled request would otherwise look
        // like an empty result set and blank the grid / reset back to page 1.
        return undefined;
       }
    },
    resetSelectedShareItems:
    () =>
      ({ setState }: StoreApi) => {
        setState({
          selectedAgencyItems: [],
        });
      },
    deleteShareDetail: (inventoryShareDetailID: number, inventoryShareID: number) => async ({ setState }: StoreApi) => {
        try {
            setState({ isLoading: true, error: null });
            await axiosSecuredInstance.delete(
                `/InventoryShare/DeleteDetail?inventoryShareDetailID=${inventoryShareDetailID}&inventoryShareID=${inventoryShareID}`,
            );
            return true;
        } catch (error: any) {
            setState({ error });
            return false;
        } finally {
            setState({ isLoading: false });
        }
    },
    exportFiles: (criteria: InventorySharedExportCriteria) => async ({ setState }: StoreApi) => {
        const getExportFilename = (): string =>
            `Inventory Share ${dateTimeAsFilename()}.${DownloadFileType.Xlsx}`;

        try {
            setState({ isLoading: true, error: null });
            const { data } = await axiosSecuredInstance.post<string>("/InventoryShare/Export", criteria);
            const filename = getExportFilename();
            downloadByBase64(`data:${getBase64DataType(DownloadFileType.Xlsx)};base64,${data}`, filename);
        } catch (error: any) {
            setState({ error });
        } finally {
            setState({ isLoading: false });
        }
    },
    updateSelectedShareItems: (items: InventorySharedUI[]) => ({ setState, getState }: StoreApi) => {
        const currentAgencyItems = getState().selectedAgencyItems || [];
        const newAgencyItems = items.filter(
            (item) =>
                !currentAgencyItems?.find(
                    (currentAgencyItem: InventorySharedUI) => currentAgencyItem.inventoryShareDetailID === item.inventoryShareDetailID,
                ),
        );

        let updatedAgencyItems: InventorySharedUI[] = [];
        if (newAgencyItems.length > 0) {
            updatedAgencyItems = currentAgencyItems?.concat(newAgencyItems);
        }

        setState({
            selectedAgencyItems: updatedAgencyItems,
        });
    },
    updateSelectedShareItem: (item: InventorySharedUI) => ({ getState, setState }: StoreApi) => {
        const currentAgencyItems = getState().selectedAgencyItems || [];
        const updatedAgencyItems = currentAgencyItems.filter(
            (currentAgencyItem: InventorySharedUI) => currentAgencyItem.inventoryShareDetailID !== item.inventoryShareDetailID,
        );

        if (updatedAgencyItems.length === currentAgencyItems.length) {
            updatedAgencyItems.push(item);
        }

        setState({
            selectedAgencyItems: updatedAgencyItems,
        });
    },
    cancelRequests:
    () =>
      async ({ getState }: StoreApi) => {
        const token = getState().axiosCancelToken;
        if (token) {
          token.cancel(" Lookup was requested to be cancelled");
        }
      },
}

const sharedTabStore = createStore<State, Actions>({
    initialState,
    actions,
    name: "InventoryShareContext",
});

const useSharedTabStore = createHook(sharedTabStore);

export default useSharedTabStore;